import React from 'react';
import { SectionHeader, SubsectionHeader } from './SectionHeader';
import { CardContainer } from './CardContainer';
import { GridLayout } from './GridLayout';
import { DocumentCard, DocumentStatus, DocumentType } from '../design-system/DocumentCard';
import { spacing } from '../design-system/tokens';

// Documents icon SVG (matching design system style)
const DocumentsIcon: React.FC = () => (
  <svg 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <polyline 
      points="14,2 14,8 20,8" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <line 
      x1="16" 
      y1="13" 
      x2="8" 
      y2="13" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <line 
      x1="16" 
      y1="17" 
      x2="8" 
      y2="17" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

export interface DocumentItem {
  id: string;
  title: string;
  type: DocumentType;
  status: DocumentStatus;
  date?: string;
  fileSize?: string;
  url?: string;
  description?: string;
}

export interface DocumentSection {
  title: string;
  documents: DocumentItem[];
}

export interface DocumentsData {
  sections: DocumentSection[];
  lastUpdated: string;
  totalDocuments: number;
  completedDocuments: number;
}

interface DocumentsCardProps {
  data: DocumentsData;
  onDocumentClick?: (document: DocumentItem) => void;
  className?: string;
}

const DocumentsCard: React.FC<DocumentsCardProps> = ({ 
  data, 
  onDocumentClick,
  className = '' 
}) => {
  const handleDocumentClick = (document: DocumentItem) => {
    if (onDocumentClick) {
      onDocumentClick(document);
    } else if (document.url) {
      window.open(document.url, '_blank');
    }
  };

  const getStatusCounts = () => {
    const counts = { complete: 0, missing: 0, pending: 0, draft: 0 };
    data.sections.forEach(section => {
      section.documents.forEach(doc => {
        counts[doc.status]++;
      });
    });
    return counts;
  };

  const statusCounts = getStatusCounts();
  const completionPercentage = data.totalDocuments > 0 
    ? Math.round((data.completedDocuments / data.totalDocuments) * 100) 
    : 0;

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: spacing.xl }}>
      <CardContainer variant="secondary">
        {/* Documents Header */}
        <SectionHeader 
          title="Documents"
          icon={<div style={{ width: '24px', height: '24px' }}><DocumentsIcon /></div>}
          showBottomBorder={true}
        />

        {/* Summary Section */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: spacing.lg,
          padding: spacing.md,
          backgroundColor: '#F9FAFB',
          borderRadius: '6px'
        }}>
          <div style={{ display: 'flex', gap: spacing.xl }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937' }}>
                {completionPercentage}%
              </div>
              <div style={{ fontSize: '12px', color: '#6B7280' }}>Complete</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937' }}>
                {data.completedDocuments}/{data.totalDocuments}
              </div>
              <div style={{ fontSize: '12px', color: '#6B7280' }}>Documents</div>
            </div>
          </div>
          <div style={{ fontSize: '12px', color: '#6B7280' }}>
            Last Updated: {data.lastUpdated}
          </div>
        </div>

        {/* Status Overview */}
        {(statusCounts.missing > 0 || statusCounts.pending > 0 || statusCounts.draft > 0) && (
          <div style={{ 
            marginBottom: spacing.lg,
            display: 'flex', 
            gap: spacing.md,
            flexWrap: 'wrap'
          }}>
            {statusCounts.complete > 0 && (
              <span style={{ 
                fontSize: '12px', 
                color: '#059669',
                backgroundColor: '#D1FAE5',
                padding: '4px 8px',
                borderRadius: '4px'
              }}>
                {statusCounts.complete} Complete
              </span>
            )}
            {statusCounts.pending > 0 && (
              <span style={{ 
                fontSize: '12px', 
                color: '#D97706',
                backgroundColor: '#FEF3C7',
                padding: '4px 8px',
                borderRadius: '4px'
              }}>
                {statusCounts.pending} Pending
              </span>
            )}
            {statusCounts.draft > 0 && (
              <span style={{ 
                fontSize: '12px', 
                color: '#D97706',
                backgroundColor: '#FEF3C7',
                padding: '4px 8px',
                borderRadius: '4px'
              }}>
                {statusCounts.draft} Draft
              </span>
            )}
            {statusCounts.missing > 0 && (
              <span style={{ 
                fontSize: '12px', 
                color: '#DC2626',
                backgroundColor: '#FEE2E2',
                padding: '4px 8px',
                borderRadius: '4px'
              }}>
                {statusCounts.missing} Missing
              </span>
            )}
          </div>
        )}

        {/* Document Sections */}
        {data.sections.map((section, sectionIndex) => (
          <div key={`section-${sectionIndex}`}>
            <SubsectionHeader title={section.title} />
            <GridLayout columns="repeat(auto-fill, minmax(200px, 1fr))">
              {section.documents.map((document) => (
                <DocumentCard
                  key={document.id}
                  title={document.title}
                  type={document.type}
                  status={document.status}
                  date={document.date}
                  fileSize={document.fileSize}
                  onClick={() => handleDocumentClick(document)}
                />
              ))}
            </GridLayout>
          </div>
        ))}

        {/* Empty State */}
        {data.sections.length === 0 && (
          <div style={{ 
            textAlign: 'center',
            padding: spacing.xl,
            color: '#6B7280'
          }}>
            <DocumentsIcon />
            <div style={{ marginTop: spacing.md }}>
              No documents available for this contract
            </div>
          </div>
        )}
      </CardContainer>
    </div>
  );
};

export default DocumentsCard;